/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef, useState } from 'react';
import {
  Col, Modal, Row, Space,
} from 'antd';
import Map from 'ol/Map';
import View from 'ol/View';
import { Vector as VectorLayer } from 'ol/layer';
import { ImageStatic, Vector as VectorSource } from 'ol/source';
import Feature, { FeatureLike } from 'ol/Feature';
import Point from 'ol/geom/Point';
import {
  Style,
  Icon,
} from 'ol/style';
import { Select } from 'ol/interaction';
import 'ol/ol.css';
import { Geometry } from 'ol/geom';
import ImageLayer from 'ol/layer/Image';
import { SelectEvent } from 'ol/interaction/Select';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { DisplaySeatDTO, EventSeatStatus } from '../models/seat';
import Button from './atoms/Button';
import { Text } from './Text';
import { RootState, useReserveSeatMutation } from '../store';
import VenueDTO from '../models/venue';
import ErrorMessage from './atoms/ErrorMessage';
import TicketDTO from '../models/ticket';

interface Props {
  seatsInfo: DisplaySeatDTO[] | null | undefined;
  selectedSeats: DisplaySeatDTO[];
  onSeatsUpdate: (selectedSeats: DisplaySeatDTO[]) => void;
  venue?: VenueDTO;
  tickets: TicketDTO[];
}

const RenderStageMap: React.FC<Props> = ({
  seatsInfo, selectedSeats, onSeatsUpdate, venue, tickets,
}) => {
  const isBackoffice = useLocation().pathname.includes('admin');
  const [updatedArray, setUpdatedArray] = useState<Array<DisplaySeatDTO>>([]);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [selectedSeat, setSelectedSeat] = useState<DisplaySeatDTO | null>(null);
  const mapElement = useRef<HTMLDivElement | null>(null);
  const selectInteractionRef = useRef<Select | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [reserveSeat, { isLoading: isReserveLoading }] = useReserveSeatMutation();
  const reservation = useSelector((state: RootState) => state.orders.reservation);
  const event = useSelector((state: RootState) => state.events.selectedEvent);
  const viewStateRef = useRef<{ center: number[], zoom: number } | null>(null);
  const isFeatureUnavailable = (feature: FeatureLike) => feature.get('status') !== EventSeatStatus.Available || (!isBackoffice && !feature.get('ticket_active'));

  // Compute per-ticket selection limit state for the currently selected seat
  const selectedSeatTicketLimit = (() => {
    if (!event || !selectedSeat) {
      return null;
    }
    const ticket = tickets.find((t) => t.id === selectedSeat.ticket_id);
    return ticket?.max_selection_allowed ?? null;
  })();
  const selectedSeatTicketCount = selectedSeat
    ? updatedArray.filter((s) => s.ticket_id === selectedSeat.ticket_id).length
    : 0;
  const isOverTicketLimit = !!selectedSeat && selectedSeatTicketLimit !== null
    ? selectedSeatTicketCount > selectedSeatTicketLimit
    : false;

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const selectedIconUrl = '/check-circle.svg';
    const getSeatIcon = (feature: FeatureLike) => {
      const seatColor = feature.get('sector_color');
      const svg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 6C0 9.31371 2.68629 12 6 12C9.31371 12 12 9.31371 12 6V0H0V6Z" fill="${seatColor}"/>
      </svg>`;
      const seatId = feature.get('id');
      const isSelected = selectedSeats.find((seat) => seat.id === seatId);
      if (isSelected) {
        return selectedIconUrl;
      }
      if (isFeatureUnavailable(feature)) {
        return '/seat.svg';
      }

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    };

    if (mapElement.current) {
      if (mapRef.current) {
        const currentView = mapRef.current.getView();
        viewStateRef.current = {
          center: currentView.getCenter() || [0, 0],
          zoom: currentView.getZoom() || 2,
        };
      }

      const view = new View({
        projection: 'EPSG:3857',
        center: viewStateRef.current?.center || [0, 0],
        zoom: viewStateRef.current?.zoom || 2,
        rotation: 0,
        enableRotation: false,
        constrainRotation: false,
      });

      const imageUrl = venue?.image_url;
      const imageExtent = JSON.parse(venue?.image_extent || '');

      const imageLayer = new ImageLayer({
        source: new ImageStatic({
          url: imageUrl || '',
          imageExtent,
        }),
        zIndex: 0,
      });

      const vectorSource = new VectorSource();
      const seatFeatures: Feature<Geometry>[] = [];

      seatsInfo?.forEach((seat) => {
        const seatFeature = new Feature({
          ...seat,
          geometry: new Point([seat.x_coordinate, seat.y_coordinate]),
          id: seat.id,
        });
        seatFeatures.push(seatFeature);
      });

      vectorSource.addFeatures(seatFeatures);

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        // When decluttering is enabled, overlapping icons may be hidden,
        // which makes it hard to see the selected seat among dense points.
        declutter: false,
        style: (feature: FeatureLike, resolution: number) => {
          const iconScale = Math.max(0.4, 0.4 / resolution);
          const iconUrl = getSeatIcon(feature);
          return new Style({
            image: new Icon({
              anchor: [0.5, 1],
              anchorXUnits: 'fraction',
              anchorYUnits: 'fraction',
              src: iconUrl,
              scale: iconScale,
            }),
            zIndex: 1,
          });
        },
        zIndex: 1,
      });

      const map = new Map({
        target: mapElement.current,
        layers: [imageLayer, vectorLayer],
        view,
      });

      const seatExtent = vectorSource.getExtent();
      if (!vectorSource.isEmpty() && !viewStateRef.current) {
        map.getView().fit(seatExtent, {
          size: map.getSize(),
          padding: [50, 50, 50, 50],
        });

        const fittedZoom = view.getZoom();
        if (fittedZoom) {
          view.setMinZoom(fittedZoom - 0.5);
        }
      }

      mapRef.current = map;
      const selectInteraction = new Select({
        hitTolerance: 6,
        style: (_: FeatureLike, resolution: number) => new Style({
          image: new Icon({
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: selectedIconUrl,
            scale: Math.max(0.4, 0.4 / resolution),
          }),
          zIndex: 10,
        }),
      });

      map.addInteraction(selectInteraction);

      selectInteraction.on('select', (e: SelectEvent) => {
        if (e.selected.length > 0) {
          const feature = e.selected[0];
          const seatInfo = feature.getProperties();

          if (isFeatureUnavailable(feature)) {
            selectInteraction.getFeatures().clear();
            return;
          }

          setSelectedSeat(seatInfo as DisplaySeatDTO);

          const selected = selectedSeats.find((seat) => seat?.id === seatInfo?.id);
          if (selected) {
            setUpdatedArray(selectedSeats.filter((seat) => seat.id !== seatInfo.id));
          } else {
            setUpdatedArray([...selectedSeats, seatInfo as DisplaySeatDTO]);
            setShowConfirmModal(true);
          }
        }
      });

      return () => {
        map.setTarget(undefined);
        selectInteractionRef.current = null;
      };
    }
  }, [seatsInfo, selectedSeats]);

  const handleCancel = () => {
    const map = mapRef.current;
    if (map) {
      const selectInteraction = map
        .getInteractions()
        .getArray()
        .find((interaction) => interaction instanceof Select) as Select;

      selectInteraction?.getFeatures().clear();
    }

    setUpdatedArray((prev) => prev.filter(() => false));
    setShowConfirmModal(false);
  };

  const handleConfirm = async () => {
    // Guard: prevent confirming if selection exceeds per-ticket limit
    if (isOverTicketLimit) {
      return;
    }
    if (!reservation || !event || !selectedSeat) {
      console.error('required data to reserve seat not found', { reservation, event, selectedSeat });
      throw new Error('required data to reserve seat not found');
    }
    const response = await reserveSeat({
      reservationCode: reservation.code,
      businessId: event.business_id,
      eventId: event.id,
      eventSeatId: selectedSeat.id,
    });

    if (!('error' in response)) {
      setShowConfirmModal(false);
      onSeatsUpdate(updatedArray);
    }
  };

  return <>
    <div ref={mapElement} className='map-container bg-white/70 rounded-3xl' style={{ height: '500px' }} />

    <Modal
      open={showConfirmModal}
      onOk={handleConfirm}
      onCancel={handleCancel}
      width={400}
      className="bg-surface-base rounded-3xl p-0"
      wrapClassName='bg-blur bg-opacity-medium p-0'
      closeIcon={null}
      styles={{
        content: {
          backgroundColor: 'rgb(var(--color-surface-base))',
          borderRadius: '32px !important',
        },
      }}
      footer={() => (
        <Row gutter={12}>
          <Col span={12}>
            <Button className='w-full' disabled={isReserveLoading} key="back" variant='secondary' onClick={handleCancel}>
              Cancelar
            </Button>
          </Col>
          <Col span={12}>
            <Button className='w-full' disabled={isOverTicketLimit} loading={isReserveLoading} key="submit" variant="primary" onClick={handleConfirm}>
              Confirmar
            </Button>
          </Col>
        </Row>
      )}>
      <Space direction='vertical' className='bg-surface-base rounded-3xl'>
        <Text size='lg' semibold className='text-text-primary'>Seleccionar asiento</Text>
        <Text size='md' className='text-text-secondary'>Seleccionaste el {selectedSeat?.seat_name} correspondiente al sector {selectedSeat?.sector_name}</Text>
        {isOverTicketLimit && (
          <ErrorMessage message={`No podés seleccionar más de ${selectedSeatTicketLimit} butaca${selectedSeatTicketLimit === 1 ? '' : 's'} para esta entrada.`} />
        )}
      </Space>
    </Modal>
  </>;
};

export default RenderStageMap;
